import { FiMail, FiUsers, FiStar } from 'react-icons/fi';
import dayjs from 'dayjs';
import { processAvatarUrl } from '../utils/avatarUtils';
import { getContactPickerPrimaryLabel } from '../utils/formatters';
import { ConversationStatusBadge, resolveConversationStatus, isMendsVisionEnabled } from '../mendsAi';
import { ChannelBadge } from './metaConversation/ChannelBadge';

interface ContactItemProps {
  contact: {
    id: number;
    name: string;
    number: string;
    email?: string;
    avatarUrl: string | null;
    lastMessage?: string;
    lastMessageTime?: string;
    tagId?: number | null;
    tagColor?: string;
    isGroup?: boolean;
    isFavorite?: boolean;
    contactStatus?: 'Novo' | 'Qualificado' | 'Não qualificado' | string;
    aiActive?: number;
    aiAgentName?: string | null;
    unreadCount?: number;
    /** Última mensagem veio do Instagram/Messenger: selo na prévia. */
    lastMessageChannel?: string | null;
  };
  isSelected: boolean;
  onSelect: () => void;
  hasNewMessage?: boolean;
  onClearNewMessage?: (contactId: number) => void;
  onContextMenu: (e: React.MouseEvent, contact: any) => void;
}

export const ContactItem: React.FC<ContactItemProps> = ({
  contact,
  isSelected,
  onSelect,
  hasNewMessage = false,
  onClearNewMessage,
  onContextMenu
}) => {
  const displayName = getContactPickerPrimaryLabel(contact.name, contact.number);

  // Mostra o número só quando é um telefone real (cru, sem máscara). Alguns contatos guardam uma
  // identidade interna do WhatsApp (LID, ex. "2464...@lid") ou id de grupo no lugar do telefone —
  // nesses casos (tem "@"/letras ou tamanho fora de 8–15 dígitos) não exibimos nada.
  const rawNumber = (contact.number || '').trim();
  const isRealPhone = /^\+?\d{8,15}$/.test(rawNumber);

  const handleClick = () => {
    if (hasNewMessage && onClearNewMessage) {
      onClearNewMessage(contact.id);
    }
    onSelect();
  };

  return (
    <div
      className={`citem-container ${isSelected ? 'citem-selected' : ''} ${hasNewMessage ? 'citem-has-new' : ''}`}
      onClick={handleClick}
      onContextMenu={(e) => {
        e.preventDefault();
        onContextMenu(e, contact);
      }}
    >
      <div className="citem-avatar">
        <img
          src={processAvatarUrl(contact.avatarUrl) || (contact.isGroup
            ? `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName || contact.name)}&background=random&length=2`
            : '/user.png')}
          alt={displayName}
          style={contact.tagId ? { borderColor: contact.tagColor } : undefined}
        />
        {hasNewMessage && (
          <div className="citem-badge" />
        )}
      </div>

      <div className="citem-info">
        <div className="citem-header">
          <div className="citem-name-wrap">
            {contact.isFavorite && <FiStar className="citem-star" size={14} fill="#FFD700" color="#FFD700" />}
            {contact.isGroup && <FiUsers className="citem-group-icon" size={14} />}
            <span className="citem-name">{displayName}</span>
          </div>
          <span className="citem-time" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {!contact.isGroup && isMendsVisionEnabled() && contact.aiActive !== undefined && (
              <ConversationStatusBadge
                compact
                status={resolveConversationStatus(
                  Number(contact.aiActive),
                  contact.unreadCount,
                  hasNewMessage
                )}
                agentName={contact.aiAgentName}
              />
            )}
            {contact.lastMessageTime ? dayjs(contact.lastMessageTime).format('HH:mm') : ''}
          </span>
        </div>

        {!contact.isGroup && isRealPhone && rawNumber !== displayName.trim() && (
          <span className="citem-number">{rawNumber}</span>
        )}

        <div className="citem-msg-wrap">
          {(contact.lastMessageChannel === 'instagram' || contact.lastMessageChannel === 'messenger' || contact.lastMessageChannel === 'webchat') && (
            <span className="citem-channel"><ChannelBadge channel={contact.lastMessageChannel} compact iconOnly label={contact.lastMessageChannel === 'instagram' ? 'Instagram' : contact.lastMessageChannel === 'webchat' ? 'Site' : 'Messenger'} /></span>
          )}
          <span className="citem-preview">
            {contact.lastMessage || 'No messages'}
          </span>
        </div>

        <div className="citem-details">
          {contact.email && (
            <span className="citem-email" title={contact.email}>
              <FiMail size={12} />
              {contact.email}
            </span>
          )}
          {(contact.contactStatus === 'Qualificado' || contact.contactStatus === 'Não qualificado') && (
            <span className="citem-status" style={{
              backgroundColor: contact.contactStatus === 'Qualificado' ? 'var(--success-bg)' : 'var(--error-bg)',
              color: contact.contactStatus === 'Qualificado' ? 'var(--success-color)' : 'var(--error-color)',
              padding: '1px 6px',
              borderRadius: '8px',
              fontSize: '0.7rem',
              marginLeft: '4px',
              display: 'inline-flex',
              alignItems: 'center'
            }}>
              {contact.contactStatus === 'Qualificado' ? 'Qualificado' : 'Não qualificado'}
            </span>
          )}
        </div>

      </div>
    </div >
  );
}; 